import type { ColDef } from "~/components/grid/AgGrid";
import type { MruFilterEntry, UseMruFiltersReturn } from "~/hooks/useMruFilters";

import {
	alpha,
	Box,
	Button,
	Chip,
	Divider,
	Icon,
	IconButton,
	Menu,
	MenuItem,
	Popover,
	Typography
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { BodyTextSmall } from "~/components/Typographies";
import clearGridFilters from "~/helpers/grid/clearGridFilters";
import { mruEntryDisplayString } from "~/helpers/grid/filterModelToDisplayString";
import filterModelToText, {
	FilterModelMap,
	resolveColumnDisplayNameFromApi,
	SetFilterLabelResolver
} from "~/helpers/grid/filterModelToText";
import migrateStoredFilterModel from "~/helpers/grid/migrateStoredFilterModel";

interface MruFilterBarProps {
	hasRibbon?: boolean;
	columnDefs: ColDef[];
	gridRef: React.RefObject<AgGridReact>;
	labelResolver?: SetFilterLabelResolver;
	mruFilters: UseMruFiltersReturn;
	onFilterApplied: () => void;
	onPausedChange?: (paused: boolean) => void;
	pathname: string;
}

const MruFilterBar = ({
	columnDefs,
	hasRibbon = true,
	gridRef,
	labelResolver,
	mruFilters,
	onFilterApplied,
	onPausedChange,
	pathname
}: MruFilterBarProps) => {
	const { t } = useTranslation("common");
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);
	const [filtersPaused, setFiltersPaused] = useState(false);
	const pausedFilterModel = useRef<FilterModelMap | null>(null);

	const api = gridRef.current?.api;
	const getDisplayName = resolveColumnDisplayNameFromApi(api);
	const filterModel = (api?.getFilterModel() as FilterModelMap | undefined) ?? null;
	const hasFilters = filterModel !== null && Object.keys(filterModel).length > 0;
	const segments = filterModelToText(filterModel, columnDefs, pathname, labelResolver, getDisplayName);

	// Show paused chips when filters are deactivated
	const pausedSegments =
		filtersPaused && pausedFilterModel.current
			? filterModelToText(pausedFilterModel.current, columnDefs, pathname, labelResolver, getDisplayName)
			: [];

	const handleToggleFilters = useCallback(() => {
		const gridApi = gridRef.current?.api;
		if (!gridApi) return;

		if (filtersPaused && pausedFilterModel.current) {
			gridApi.setFilterModel(pausedFilterModel.current);
			pausedFilterModel.current = null;
			setFiltersPaused(false);
			onPausedChange?.(false);
		} else {
			const currentModel = gridApi.getFilterModel() as FilterModelMap | null;
			if (currentModel && Object.keys(currentModel).length > 0) {
				pausedFilterModel.current = currentModel;
				void clearGridFilters(gridApi);
				setFiltersPaused(true);
				onPausedChange?.(true);
			}
		}
		onFilterApplied();
	}, [filtersPaused, onFilterApplied, onPausedChange, gridRef]);

	const handleClearAll = useCallback(() => {
		const gridApi = gridRef.current?.api;
		if (!gridApi) return;
		void clearGridFilters(gridApi);
		pausedFilterModel.current = null;
		setFiltersPaused(false);
		onPausedChange?.(false);
		onFilterApplied();
	}, [onFilterApplied, onPausedChange, gridRef]);

	const handleClearColumn = useCallback(
		(field: string) => {
			const gridApi = gridRef.current?.api;
			if (!gridApi) return;
			void clearGridFilters(gridApi, field);
			onFilterApplied();
		},
		[onFilterApplied, gridRef]
	);

	const handleApplyEntry = useCallback(
		(entry: MruFilterEntry) => {
			const gridApi = gridRef.current?.api;
			if (!gridApi) return;
			gridApi.setFilterModel(migrateStoredFilterModel(entry.filterModel, columnDefs) ?? null);
			pausedFilterModel.current = null;
			setFiltersPaused(false);
			onPausedChange?.(false);
			onFilterApplied();
			setMenuAnchor(null);
		},
		[columnDefs, onFilterApplied, onPausedChange, gridRef]
	);

	const handleClearHistory = useCallback(() => {
		mruFilters.clearEntries();
		setMenuAnchor(null);
	}, [mruFilters.clearEntries]);

	const displaySegments = filtersPaused ? pausedSegments : segments;
	const showActive = hasFilters && !filtersPaused;

	return (
		<Box
			aria-label={t("grid.filterBar.helpTitle")}
			component="section"
			sx={{
				backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
				borderBottom: 1,
				borderColor: "divider",
				flexShrink: 0,
				px: 2,
				pb: displaySegments.length ? 1.5 : 0
			}}
		>
			<Box
				sx={{
					"@media (max-width: 700px)": { alignItems: "flex-start", flexDirection: "column", py: 1 },
					alignItems: "center",
					display: "flex",
					flexDirection: "row",
					gap: 1,
					justifyContent: "space-between",
					minHeight: 48
				}}
			>
				<Box sx={{ alignItems: "center", color: "primary.main", display: "flex", gap: 0.75 }}>
					<Icon sx={{ fontSize: 18 }}>filter_list</Icon>
					<BodyTextSmall role="status" sx={{ fontSize: "0.75rem", fontWeight: 600, lineHeight: 1.5 }}>
						{filtersPaused
							? t("grid.filterBar.filtersPaused")
							: hasFilters
								? t("grid.filterBar.activeCount", { count: segments.length })
								: t("grid.filterBar.noActiveFilters")}
					</BodyTextSmall>
					<IconButton
						aria-label={t("grid.filterBar.about")}
						onClick={(e) => setHelpAnchor(e.currentTarget)}
						size="small"
						sx={{ p: 0.25 }}
					>
						<Icon sx={{ fontSize: 16 }}>help_outline</Icon>
					</IconButton>
				</Box>
				<Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					<Button
						aria-pressed={filtersPaused}
						disabled={!hasFilters && !filtersPaused}
						onClick={handleToggleFilters}
						size="small"
						startIcon={<Icon>{showActive ? "pause" : "play_arrow"}</Icon>}
						sx={{ fontSize: "0.75rem", textTransform: "none" }}
					>
						{filtersPaused ? t("grid.filterBar.resumeFilters") : t("grid.filterBar.pauseFilters")}
					</Button>
					<Button
						aria-expanded={Boolean(menuAnchor)}
						aria-haspopup="menu"
						endIcon={<Icon>arrow_drop_down</Icon>}
						onClick={(e) => setMenuAnchor(e.currentTarget)}
						size="small"
						startIcon={<Icon>history</Icon>}
						sx={{ fontSize: "0.75rem", textTransform: "none" }}
						variant="outlined"
					>
						{t("grid.filterBar.recentFilters")}
					</Button>
					<Button
						disabled={!hasFilters && !filtersPaused}
						onClick={handleClearAll}
						size="small"
						sx={{ fontSize: "0.75rem", textTransform: "none" }}
						variant="contained"
					>
						{t("grid.filterBar.clearAll")}
					</Button>
				</Box>
			</Box>
			{displaySegments.length > 0 && (
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						flexWrap: "wrap",
						gap: 0.875,
						"@media (max-width: 700px)": { pl: 0 },
						pl: 3
					}}
				>
					{displaySegments.map((seg, index) => (
						<Chip
							deleteIcon={<Icon aria-label={t("grid.filterBar.clearColumn", { column: seg.label })}>close</Icon>}
							key={seg.field}
							label={
								<>
									{index > 0 && (
										<Box component="span" sx={{ color: "text.disabled", fontSize: "0.625rem", pr: 0.75 }}>
											{t("grid.filterText.and")}
										</Box>
									)}
									<Box component="span" sx={{ fontWeight: 600 }}>
										{seg.label}:
									</Box>{" "}
									{seg.text}
								</>
							}
							onDelete={filtersPaused ? undefined : () => handleClearColumn(seg.field)}
							size="small"
							sx={{
								"& .MuiChip-label": { overflowWrap: "anywhere", py: 0.75, whiteSpace: "normal" },
								backgroundColor: "background.paper",
								fontSize: "0.75rem",
								height: "auto",
								maxWidth: "100%",
								opacity: filtersPaused ? 0.5 : 1
							}}
							variant="outlined"
						/>
					))}
				</Box>
			)}
			<Menu
				anchorEl={menuAnchor}
				anchorOrigin={{ horizontal: "right", vertical: "top" }}
				onClose={() => setMenuAnchor(null)}
				open={Boolean(menuAnchor)}
				transformOrigin={{ horizontal: "right", vertical: "bottom" }}
			>
				{mruFilters.entries.length === 0 ? (
					<MenuItem disabled>
						<Typography sx={{ fontSize: "0.8rem" }}>{t("grid.filterBar.noHistory")}</Typography>
					</MenuItem>
				) : (
					[
						...mruFilters.entries.map((entry) => (
							<MenuItem
								key={entry.id}
								onClick={() => handleApplyEntry(entry)}
								sx={{ fontSize: "0.8rem", maxWidth: 500 }}
							>
								<Typography noWrap sx={{ fontSize: "0.8rem" }}>
									{mruEntryDisplayString(entry, columnDefs, pathname, labelResolver, getDisplayName)}
								</Typography>
							</MenuItem>
						)),
						<Divider key="divider" />,
						<MenuItem key="clear" onClick={handleClearHistory}>
							<Typography sx={{ color: "error.main", fontSize: "0.8rem" }}>
								{t("grid.filterBar.clearHistory")}
							</Typography>
						</MenuItem>
					]
				)}
			</Menu>
			<Popover
				anchorEl={helpAnchor}
				anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
				onClose={() => setHelpAnchor(null)}
				open={Boolean(helpAnchor)}
				transformOrigin={{ horizontal: "right", vertical: "top" }}
			>
				<Box sx={{ maxWidth: 320, p: 1.5 }}>
					<BodyTextSmall bold>{t("grid.filterBar.helpTitle")}</BodyTextSmall>
					<BodyTextSmall sx={{ mt: 0.5 }}>{t("grid.filterBar.helpSummary")}</BodyTextSmall>
					<BodyTextSmall sx={{ mt: 1 }}>
						{t(hasRibbon ? "grid.filterBar.helpPlacement" : "grid.filterBar.helpPlacementEmbedded")}
					</BodyTextSmall>
					<BodyTextSmall sx={{ mt: 1 }}>{t("grid.filterBar.helpSaved")}</BodyTextSmall>
				</Box>
			</Popover>
		</Box>
	);
};

export default MruFilterBar;
